import { useState } from "react"

import type { SortEnum } from "@arbeidsark/shared"
import identity from "lodash/identity"
import isFunction from "lodash/isFunction"
import styled from "styled-components"
import { TableContentLoader } from "~/components/Table"
import type { BaseProps } from "~/types/props"
import { Card, type CardProps } from "~/ur-react-components"
import { Table } from "./Table"
import { TableBody } from "./TableBody"
import { TableCell, type TableCellProps } from "./TableCell"
import { TableHead } from "./TableHead"
import { TableHeaderCell } from "./TableHeaderCell"
import { TableHeaderCellSortIcon } from "./TableHeaderCellSortIcon"
import { TableHeaderFilter } from "./TableHeaderFilter"
import { TableRow } from "./TableRow"
import type { TableProps } from "./types"

const NoData = styled.div`
  text-align: center;
`

export enum SORT {
  NONE = 0,
  ASCENDING = 1,
  DESCENDING = -1,
}

export interface QuoTableCardProps extends TableProps {
  cardProps?: CardProps
}

function getNextSortStatus(current: SortEnum | undefined) {
  switch (current) {
    case "asc":
      return "desc"
    case "desc":
      return undefined
    default:
      return "asc"
  }
}

interface BaseQuoTableProps extends TableProps, BaseProps {
  /** Text to show in own row if no data. If null or undefined, no such row is displayed. */
  noDataText?: string | null
  cellProps?: TableCellProps
}

const BaseQuoTable: React.CFC<BaseQuoTableProps> = ({
  className,
  id,

  columns,
  data,
  sort,
  filter,

  tableProps,
  headerProps,
  bodyProps,
  cellProps,
  loading,
  loadingProps,
  noDataText,

  onSortChanged,
  onFilterChanged,
}) => {
  const visibleColumns = columns.filter((column) => !column.hide)

  /* SORT HANDLING */

  // Importantly we have to use an OrderedMap here to preserve the order
  const [openColumnFilter, setOpenColumnFilter] = useState<string | null>(null)

  function updateSortStatus(name: string) {
    const currentSort = sort?.[name]
    const next = getNextSortStatus(currentSort)

    if (!next) {
      // This is just a clever way to get the entire sort without the curreny key
      const { [name]: _, ...rest } = sort || {}
      onSortChanged?.(rest)
    } else {
      onSortChanged?.({ ...sort, [name]: next })
    }
  }
  /* END SORT HANDLING */

  return (
    <Table {...tableProps} className={className} id={id}>
      <TableHead display={headerProps?.display}>
        <TableRow rowProps={{ head: true }}>
          {visibleColumns.map((column) => (
            <TableHeaderCell
              key={column.id}
              sortedStatus={sort?.[column.sortKey || column.id]}
              highlight={
                !!sort?.[column.sortKey || column.id] ||
                openColumnFilter === column.id
              }
              cursor={column.sortable || column.filterable ? "pointer" : ""}
              onClick={(e) => {
                if (column.sortable) {
                  updateSortStatus(column.sortKey || column.id)
                }

                if (column.filterable) {
                  setOpenColumnFilter(openColumnFilter ? null : column.id)
                }
                e.stopPropagation()
              }}
              width={
                column.width
                  ? column.width
                  : column.id === "rowMenu"
                    ? "1px"
                    : "auto"
              }
              padding={
                column.id === "rowMenu" ? "0  1.5625rem 0 0" : "0  1.5625rem"
              }
              {...(column.headerProps || {})}
              {...headerProps}
            >
              {column.label}
              {column.sortable && (
                <TableHeaderCellSortIcon
                  sortStatus={sort?.[column.sortKey || column.id]}
                />
              )}
              {column.filterable && (
                <TableHeaderFilter
                  open={openColumnFilter === column.id}
                  onClose={() => setOpenColumnFilter(null)}
                  values={column.filterValues ?? []}
                  activeFilters={filter ? filter[column.id] : []}
                  onFilterChanged={(newValue) => {
                    if (onFilterChanged) {
                      onFilterChanged(column.id, newValue)
                    }
                  }}
                />
              )}
            </TableHeaderCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody {...bodyProps}>
        {data.map((row) => {
          return (
            <TableRow key={row.id} rowProps={{ ...row.props }}>
              {visibleColumns.map((column) => {
                const columnValue = (row.data as { [key: string]: number })[
                  column.id
                ]
                if (isFunction(columnValue)) {
                  return columnValue()
                }
                return column.id === "rowMenu" ? (
                  <TableCell
                    key={column.id}
                    onClick={row.props?.onClick}
                    width={"1px"}
                    padding={"0 1.5625rem 0 0"}
                  >
                    {columnValue}
                  </TableCell>
                ) : column.id === "favourited" ? (
                  <TableCell
                    key={column.id}
                    onClick={row.props?.onClick}
                    padding={"0px 0px 0px 2rem"}
                    width="1px"
                  >
                    {columnValue}
                  </TableCell>
                ) : (
                  <TableCell
                    key={column.id}
                    {...cellProps}
                    onClick={row.props?.onClick}
                  >
                    {columnValue}
                  </TableCell>
                )
              })}
            </TableRow>
          )
        })}
        {loading && (
          <TableContentLoader columns={columns.length} {...loadingProps} />
        )}
        {!loading && data.length === 0 && noDataText && (
          <TableRow>
            <TableCell colSpan={9999}>
              <NoData>{noDataText}</NoData>
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  )
}

export const QuoTableCard: React.CFC<QuoTableCardProps> = ({
  cardProps,
  ...rest
}) => {
  return (
    <Card {...cardProps}>
      <BaseQuoTable {...rest} />
    </Card>
  )
}

BaseQuoTable.defaultProps = {
  tableProps: {},
  onSortChanged: identity,
}

const StyledQuoTable = styled(BaseQuoTable)`
  tr {
    .menu-icon {
      visibility: hidden;
      padding: 0 12px;
      cursor: pointer;
    }
    &:hover {
      .menu-icon {
        visibility: visible;

        &:hover {
          color: ${(props) => props.theme.colors.secondary};
        }
      }
    }
    span.sub {
      vertical-align: -25%;
      font-size: 0.7em;
    }
  }
`
export type { BaseQuoTableProps as QuoTableProps }
export { StyledQuoTable as QuoTable }
